import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Sun, Wind, Heart, ArrowRight, Check } from "lucide-react";
import Layout from "../components/Layout";
import { EText } from "../components/cms/Editable";

const QUIZZES = [
  { title: "Thinking Style Quiz", key: "thinking-style", desc: "Discover whether you lead with Male, Female, or Mixed thinking energy." },
  { title: "5 Seasons — Who Am I?", key: "5-seasons", desc: "Reveal your dominant element and seasonal personality type." },
  { title: "7 Levels of Vital Health", key: "7-levels", desc: "Assess where you stand across the seven levels of vital health." },
];

const ELEMENTS = [
  { name: "Wood", org: "Growth & vision", icon: Leaf },
  { name: "Fire", org: "Joy & connection", icon: Sun },
  { name: "Earth", org: "Nourishment & center", icon: Heart },
  { name: "Metal", org: "Clarity & letting go", icon: Wind },
  { name: "Water", org: "Wisdom & flow", icon: Wind },
];

export default function ClubPage() {
  return (
    <Layout>
      <section className="section-pad bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <EText k="club.pill" className="pill bg-brand-cream2 text-brand-tan">The Zenergize Holistic Health Club</EText>
          <EText k="club.title" as="h1" className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-brand-ink mt-4 block">A Living Path to Whole-Being Health</EText>
          <EText k="club.intro" as="p" multiline className="mt-5 text-brand-inksoft leading-relaxed text-lg block">
            The Zenergize Holistic Health Club is a growing library of guided videos, remedies, assessments, and teachings — organized into three cumulative membership tiers so you can begin free and go deeper whenever you're ready.
          </EText>
        </div>
      </section>

      <section className="section-pad bg-brand-cream">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          {[
            { name: "Silver Serenity", price: "Free", text: "Introductory videos, foundational assessments, and your Free Healing Blueprint Guide." },
            { name: "Gold Genesis", price: "$25 lifetime", text: "A structured learning path, seasonal guidance, remedies, and the Shaping Our Destiny ebook." },
            { name: "Diamond Dharma", price: "$50 lifetime", text: "The full library, all trainings, emotional mastery teachings, and priority consultations." },
          ].map((c, i) => (
            <motion.div key={c.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="card-surface p-7">
              <EText k={`club.tier.${i}.name`} as="h3" className="font-display text-2xl font-semibold text-brand-ink block">{c.name}</EText>
              <EText k={`club.tier.${i}.price`} as="p" className="text-brand-gold font-medium text-sm mt-1 block">{c.price}</EText>
              <EText k={`club.tier.${i}.text`} as="p" multiline className="text-brand-inksoft text-sm mt-3 leading-relaxed block">{c.text}</EText>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/packages" className="btn-primary" data-testid="club-join-cta">Explore Membership <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* Yin/Yang & Five Elements */}
      <section className="section-pad bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <EText k="club.elements.title" as="h2" className="font-display text-3xl sm:text-4xl font-semibold text-brand-ink block">Yin, Yang & the Five Elements</EText>
            <EText k="club.elements.text" as="p" multiline className="mt-3 text-brand-inksoft max-w-2xl mx-auto block">Every teaching here is rooted in the balance of Yin and Yang and the cycle of the Five Elements — the framework that connects your food, movement, emotions, and spirit.</EText>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {ELEMENTS.map((e) => (
              <div key={e.name} className="card-surface p-5 text-center">
                <e.icon className="text-brand-sage mx-auto" />
                <h4 className="font-display text-xl font-semibold text-brand-ink mt-2">{e.name}</h4>
                <EText k={`club.element.${e.name}`} as="p" className="text-xs text-brand-muted mt-1 block">{e.org}</EText>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 120 days */}
      <section className="section-pad bg-brand-cream2">
        <div className="max-w-3xl mx-auto text-center">
          <EText k="club.days.title" as="h2" className="font-display text-3xl sm:text-4xl font-semibold text-brand-ink block">You Rebuild Yourself Every 120 Days</EText>
          <EText k="club.days.text" as="p" multiline className="mt-4 text-brand-inksoft leading-relaxed block">
            Your blood, tissues, and cells renew almost completely within roughly 120 days. That means the person you're becoming is being built right now — from the food you eat, the breath you breathe, and the energy you keep. The Club exists to make those 120 days a deliberate act of healing.
          </EText>
        </div>
      </section>

      {/* Free quizzes */}
      <section className="section-pad bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <EText k="club.quiz.pill" className="pill bg-brand-cream2 text-brand-tan">Free For Silver Members</EText>
            <EText k="club.quiz.title" as="h2" className="font-display text-3xl sm:text-4xl font-semibold text-brand-ink mt-3 block">Start With a Free Assessment</EText>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {QUIZZES.map((q) => (
              <div key={q.title} className="card-surface p-6 flex flex-col group hover:shadow-md transition-shadow">
                <EText k={`club.quiz.${q.key}.title`} as="h4" className="font-display text-xl font-semibold text-brand-ink block">{q.title}</EText>
                <EText k={`club.quiz.${q.key}.desc`} as="p" className="text-sm text-brand-inksoft mt-2 flex-1 block">{q.desc}</EText>
                <Link to={`/quiz/${q.key}`} className="btn-primary mt-4 !py-2 text-sm" data-testid={`quiz-${q.key}`}>Take the Quiz <ArrowRight size={15} /></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="px-5 sm:px-8 py-10 bg-brand-cream">
        <div className="max-w-3xl mx-auto card-surface p-6">
          <h4 className="font-semibold text-brand-ink flex items-center gap-2"><Check size={16} className="text-brand-sage" /> Medical Disclaimer</h4>
          <EText k="club.disclaimer" as="p" multiline className="text-xs text-brand-muted mt-2 leading-relaxed block">
            Isoharts is a 501(c)(3) spiritually based nonprofit organization. All education is for personal benefit and self development. Nothing offered here is intended to diagnose, treat, cure, or prevent any disease, and it is not a substitute for professional medical advice. Your consent to this disclaimer is recorded at signup and checkout.
          </EText>
        </div>
      </section>
    </Layout>
  );
}
